import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';

import 'react-quill/dist/quill.snow.css';
import { prepareEmailHtmlForQuill } from '../../Utils/quill-email';

// Quill-based email editor.
// - props.value: HTML string
// - props.onEditorStateChange(htmlString)
const EMPTY_HTMLS = new Set(['<p><br></p>', '<p></p>']);

// Use Quill-native size keys so dropdown labels work properly.
// Quill represents "normal" size as `false` (no `ql-size-*` class).
const FONT_SIZES = ['small', false, 'large', 'huge'];

const COLOR_OPTIONS = [
    '#000000',
    '#666666',
    '#e60000',
    '#0066cc',
    '#00a651',
    '#ff9900',
    '#9900ff',
];

const EmailQuillEditor = (props) => {
    const lastSentHtmlRef = useRef(props.value ?? '');
    const [value, setValue] = useState(props.value ?? '');

    const showAdvancedToolbar = props.showAdvancedToolbar !== false; // default true

    useEffect(() => {
        const nextExternal = props.value ?? '';
        const normalized = prepareEmailHtmlForQuill(nextExternal);
        if (nextExternal !== value) {
            // Quill expects `ql-size-*` classes for size rendering.
            // Some stored templates may contain inline `font-size`; normalize them back to classes.
            setValue(normalized);
        }
        // Keep the reference in sync with what Quill is actually showing.
        lastSentHtmlRef.current = normalized;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    const formats = useMemo(() => {
        const base = ['bold', 'italic', 'underline', 'align', 'list', 'bullet'];
        if (!showAdvancedToolbar) return base;
        return [...base, 'header', 'size', 'color', 'link'];
    }, [showAdvancedToolbar]);

    const modules = useMemo(() => {
        const baseToolbar = [
            ['bold', 'italic', 'underline'],
            // Use align buttons (not dropdown) to avoid "undefined" showing in toolbar.
            [{ align: 'left' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
            [{ list: 'ordered' }, { list: 'bullet' }],
        ];

        if (!showAdvancedToolbar) {
            return {
                toolbar: baseToolbar,
                clipboard: { matchVisual: false },
            };
        }

        return {
            toolbar: [
                ...baseToolbar,
                [
                    // '' => Normal (Quill uses empty string for the normal paragraph)
                    { header: ['', 1, 2, 3, 4, 5, 6] },
                    { size: FONT_SIZES },
                    { color: COLOR_OPTIONS },
                    'link',
                ],
            ],
            clipboard: { matchVisual: false },
        };
    }, [showAdvancedToolbar]);

    const normalizeHtml = (html) => {
        const trimmed = (html || '').trim();
        if (!trimmed || EMPTY_HTMLS.has(trimmed)) return '';
        return trimmed;
    };

    const handleChange = (html) => {
        const nextHtml = normalizeHtml(html);
        setValue(nextHtml);

        if (nextHtml !== lastSentHtmlRef.current) {
            lastSentHtmlRef.current = nextHtml;
            props?.onEditorStateChange?.(nextHtml);
        }
    };

    return (
        <div className={props.className}>
            {props.label && (
                <label className={`${props.labelStyle || ''} e-text-chinese-black e-font-14 e-primary-font e-font-weight-400 line-height-18px padding-4px-bottom`}>
                    {props.label}
                    {props.required && <span className="e-text-carmine-pink">*</span>}
                </label>
            )}

            <div
                data-error={props.error ? '1' : '0'}
                className={`email-quill-editor-wrap  ${props.error ? 'e-border-0-5px-carmine-pink' : 'e-border-0-5px-arsenic'} border-radius-8 h-306px`}
                style={{ position: 'relative' }}
            >
                <style>{`
                    .email-quill-editor-wrap { overflow: visible; }

                    /* Map Quill native size keys to desired em sizes */
                    .ql-size-small { font-size: 0.75em; }
                    .ql-size-normal { font-size: 1em; }
                    .ql-size-large { font-size: 1.5em; }
                    .ql-size-huge { font-size: 2.5em; }

                    /* Remove paragraph spacing inside editor */
                    .email-quill-editor .ql-editor p {
                        margin: 0 !important;
                        margin-bottom: 0 !important;
                    }

                    /* Remove Quill snow inner borders to avoid double-border/double-height */
                    .email-quill-editor .ql-toolbar.ql-snow,
                    .email-quill-editor .ql-container.ql-snow,
                    .email-quill-editor .ql-toolbar.ql-snow + .ql-container.ql-snow {
                        border: 0 !important;
                        border-radius: 0 !important;
                    }

                    /* Apply the same rounded corners as border-radius-8 */
                    .email-quill-editor .ql-toolbar.ql-snow {
                        border-top-left-radius: 0.5rem !important;
                        border-top-right-radius: 0.5rem !important;
                    }
                    .email-quill-editor .ql-container.ql-snow {
                        border-bottom-left-radius: 0.5rem !important;
                        border-bottom-right-radius: 0.5rem !important;
                    }
                    .email-quill-editor .ql-editor {
                        border-radius: 0 0 0.5rem 0.5rem;
                    }

                    /* Keep toolbar + editor inside wrapper height */
                    .email-quill-editor {
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        min-height: 0;
                    }
                    .email-quill-editor .ql-toolbar {
                        flex: 0 0 auto;
                    }
                    .email-quill-editor .ql-container {
                        flex: 1 1 auto;
                        min-height: 0;
                    }

                    /* Dropdowns/popups should not get clipped */
                    .email-quill-editor .ql-toolbar { z-index: 2; position: relative; background: white; }
                    .email-quill-editor .ql-picker-options { z-index: 9999 !important; }
                    
                    /* Border bottom under the header/toolbar */
                    .email-quill-editor-wrap[data-error="0"] .email-quill-editor .ql-toolbar.ql-snow {
                        border-bottom: 0.031rem solid #414141 !important; /* $arsenic */
                    }
                    .email-quill-editor-wrap[data-error="1"] .email-quill-editor .ql-toolbar.ql-snow {
                        border-bottom: 0.031rem solid #f04438 !important; /* $carmine_pink */
                    }
                    /* Keep editor scroll area, not the toolbar popups */
                    .email-quill-editor .ql-container { height: 100%; }
                    /* Let Quill manage internal layout; only ensure scrolling works */
                    .email-quill-editor .ql-editor { overflow-y: auto; pointer-events: auto; }

                    /* Remove violet focus border/outline coming from global focus styles */
                    .email-quill-editor-wrap[data-error="0"]:focus-within {
                        border: 0.031rem solid #414141 !important; /* $arsenic */
                    }
                    .email-quill-editor-wrap[data-error="1"]:focus-within {
                        border: 0.031rem solid #f04438 !important; /* $carmine_pink */
                    }
                    .email-quill-editor-wrap:focus-within .ql-container.ql-snow {
                        border-color: #414141 !important;
                        box-shadow: none !important;
                    }
                    .email-quill-editor-wrap[data-error="1"]:focus-within .ql-container.ql-snow {
                        border-color: #f04438 !important;
                    }
                `}</style>

                <div className="email-quill-editor">
                    <ReactQuill
                        theme="snow"
                        value={value}
                        onChange={handleChange}
                        modules={modules}
                        formats={formats}
                        placeholder=""
                    />
                </div>
            </div>

            {props.error && (
                <p className="position-absolute mb-0 e-text-carmine-pink e-primary-font e-font-12 line-height-12px pt-1 e-font-weight-500">
                    {props.error}
                </p>
            )}
        </div>
    );
};

export default memo(EmailQuillEditor);

